import React, { FC, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { RaceData } from '../../api/types';
import {
  ariaSummary,
  blockerText,
  buildRaceModel,
  buildScale,
  formatDuration,
  formatGap,
  formatSeconds,
  formatTick,
  pathOf,
  Racer,
  RaceModel,
  TimeScale,
} from './raceGraph/model';
import RacerDetail from './raceGraph/RacerDetail';
import { COLORS } from './raceGraph/theme';

/**
 * The race to the AOTD, one row per racer.
 *
 * Rows run in finishing order (winner first), and each row has its own lane
 * on a shared time axis, so racers who tapped 10ms apart never pile on top of
 * each other the way they did on a single line. The axis is linear through
 * the decisive cluster and squeezed after it (see buildScale), the right
 * column says how far behind the winner each racer finished, and a racer's
 * row opens a card with the full breakdown.
 *
 * Card head starts and the no-emoji penalty are drawn as a short line from
 * where the racer tapped (hollow dot) to where they were counted (their
 * emoji). Latency corrections are deliberately not shown anywhere.
 */

interface RaceTimelineProps {
  race: RaceData;
  generationTimeMs?: number;
  /** Play the mount animation backwards (closing the advanced view), then call onRewound. */
  rewind?: boolean;
  onRewound?: () => void;
}

const TRACK_PAD = 12;
const SWEEP_S = 1.3;
const START_S = 0.15;
const POP_S = 0.22;
const SHIFT_PAUSE_S = 0.28;
const SHIFT_STEP_S = 0.32;
// The rewind runs the same steps backwards, a little faster.
const REWIND = 0.6;

// ---- layout ----

const Wrap = styled.div`
  margin-top: 12px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const Title = styled.div`
  font-size: 0.75rem;
  font-style: italic;
  color: ${COLORS.label};
`;

const ReplayButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font: inherit;
  font-size: 0.68rem;
  color: ${COLORS.ink2};
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 999px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:hover {
    border-color: #d8ccef;
    color: ${COLORS.winnerInk};
  }
  &:focus-visible {
    outline: 2px solid ${COLORS.focus};
    outline-offset: 2px;
  }
`;

const Card = styled.div`
  position: relative;
  padding: 10px 8px 8px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  color: ${COLORS.ink};
`;

const Summary = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  padding: 0 4px 8px;
  font-size: 0.75rem;
  color: ${COLORS.ink2};
  & b {
    color: ${COLORS.ink};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px;
  column-gap: 6px;
  align-items: stretch;
`;

const AxisRow = styled(Grid)`
  height: 18px;
`;

const Track = styled.div`
  position: relative;
  min-width: 0;
`;

/** The drawable part of a track, inset so an emoji at either end isn't clipped. */
const Plot = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${TRACK_PAD}px;
  right: ${TRACK_PAD}px;
`;

const TickLabel = styled.span<{ $frac: number }>`
  position: absolute;
  left: ${(p) => p.$frac * 100}%;
  top: 2px;
  transform: translateX(-50%);
  font-size: 0.6rem;
  line-height: 1;
  color: ${COLORS.label};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Band = styled.div<{ $from: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(p) => p.$from * 100}%;
  right: -${TRACK_PAD}px;
  background: ${COLORS.tailBand};
`;

const VLine = styled.div<{ $frac: number; $color: string; $w?: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(p) => p.$frac * 100}%;
  width: ${(p) => p.$w ?? 1}px;
  transform: translateX(-50%);
  background: ${(p) => p.$color};
`;

const BreakMark = styled.svg<{ $frac: number }>`
  position: absolute;
  left: ${(p) => p.$frac * 100}%;
  bottom: 0;
  transform: translateX(-50%);
  overflow: visible;
`;

const Body = styled.div`
  position: relative;
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const RowButton = styled.button<{ $winner: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px;
  column-gap: 6px;
  width: 100%;
  height: 32px;
  padding: 0;
  font: inherit;
  text-align: left;
  color: inherit;
  background: ${(p) => (p.$winner ? '#fcfaff' : 'transparent')};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${COLORS.rowHover};
  }
  &[aria-expanded='true'] {
    background: ${COLORS.rowOpen};
  }
  &:focus-visible {
    outline: none;
  }
  /* The focus ring is drawn on top: the plot's own layers would cover an outline. */
  &:focus-visible::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid ${COLORS.focus};
    border-radius: 6px;
    pointer-events: none;
    z-index: 3;
  }

  & .race-name {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  & .race-name-inline {
    display: none;
  }
  &:hover .race-name,
  &:focus-visible .race-name,
  &[aria-expanded='true'] .race-name {
    opacity: 1;
  }
  &:hover .race-name-inline,
  &:focus-visible .race-name-inline,
  &[aria-expanded='true'] .race-name-inline {
    display: inline;
  }
`;

const RightLabel = styled.span<{ $tone: 'winner' | 'gap' | 'status' | 'early' }>`
  align-self: center;
  justify-self: end;
  padding-right: 4px;
  font-size: 0.7rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => (p.$tone === 'winner' ? 700 : 500)};
  font-style: ${(p) => (p.$tone === 'status' ? 'italic' : 'normal')};
  color: ${(p) =>
    p.$tone === 'winner' ? COLORS.ink : p.$tone === 'early' ? COLORS.earlyInk : p.$tone === 'status' ? COLORS.label : COLORS.ink2};
`;

// Three wrappers per marker: the outer one slides (left), the middle one
// centres on the point, the inner one pops. framer-motion owns `transform`
// on whatever it scales, so the centring can't live on the same element.
const Pos = styled(motion.div)`
  position: absolute;
  top: 50%;
  width: 0;
  height: 0;
`;

const Center = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
`;

const Pop = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const Ring = styled.span<{ $color: string; $wash: string }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid ${(p) => p.$color};
  background: ${(p) => p.$wash};
`;

const Glyph = styled.span<{ $dim?: boolean }>`
  position: relative;
  font-size: 0.95rem;
  line-height: 1;
  ${(p) => (p.$dim ? 'opacity: 0.5; filter: grayscale(0.6);' : '')}
`;

const Dot = styled.span<{ $color: string; $dim?: boolean }>`
  position: relative;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 0 2px ${COLORS.surface};
  ${(p) => (p.$dim ? 'opacity: 0.5;' : '')}
`;

const NamePill = styled.span<{ $side: 'left' | 'right'; $always?: boolean }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === 'right' ? 'left: calc(100% + 1px);' : 'right: calc(100% + 1px);')}
  transform: translateY(-50%);
  max-width: 120px;
  padding: 1px 5px;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${COLORS.ink};
  background: ${COLORS.surface};
  border-radius: 4px;
  box-shadow: 0 0 0 1px ${COLORS.border};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  z-index: 2;
  ${(p) => (p.$always ? 'opacity: 1 !important; box-shadow: none; background: rgba(255,255,255,0.85); font-weight: 500; color: #4b5563;' : '')}
`;

const Segment = styled(motion.div)<{ $color: string }>`
  position: absolute;
  top: 50%;
  height: 2px;
  margin-top: -1px;
  border-radius: 1px;
  background: ${(p) => p.$color};
`;

const TapDot = styled(motion.div)<{ $color: string }>`
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background: ${COLORS.surface};
  border: 1.5px solid ${(p) => p.$color};
  box-sizing: border-box;
`;

const InlineNote = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: -4px;
  max-width: calc(100% + 4px);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px 2px 4px;
  border-radius: 4px;
  background: ${COLORS.surface};
  font-size: 0.68rem;
  color: ${COLORS.muted};
  white-space: nowrap;
  overflow: hidden;
`;

const InlineName = styled.span`
  font-weight: 600;
  color: ${COLORS.ink2};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Caption = styled.div`
  margin-top: 6px;
  padding: 6px 4px 2px;
  border-top: 1px solid ${COLORS.grid};
  font-size: 0.65rem;
  font-style: italic;
  color: ${COLORS.label};
`;

const Playhead = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: linear-gradient(to bottom, rgba(147, 51, 234, 0), rgba(147, 51, 234, 0.45) 12%, rgba(147, 51, 234, 0.45) 88%, rgba(147, 51, 234, 0));
  pointer-events: none;
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 8px 4px 0;
  font-size: 0.62rem;
  color: ${COLORS.muted};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

const Hint = styled.span`
  font-style: italic;
  color: ${COLORS.label};
`;

const Swatch = styled.span`
  display: inline-block;
  width: 12px;
  height: 9px;
  border-radius: 2px;
  background: ${COLORS.tailBand};
  box-shadow: inset 0 0 0 1px #e6e6ec;
`;

// ---- pieces ----

/** A short line with a hollow dot at its start: the legend key for a shift. */
const ShiftKey: FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="8" viewBox="0 0 20 8" aria-hidden="true">
    <line x1="4" y1="4" x2="19" y2="4" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="4" cy="4" r="3" fill="#fff" stroke={color} strokeWidth="1.5" />
  </svg>
);

const ReplayIcon: FC = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M3 8a5 5 0 1 0 1.6-3.7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path d="M2.5 2.2v3.6h3.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Lines behind every row: shaded tail, gridlines, unlock line. */
const Backdrop: FC<{ scale: TimeScale }> = ({ scale }) => (
  <>
    {scale.breakFrac !== null && <Band $from={scale.breakFrac} />}
    {scale.ticks
      .filter((t) => t.ms !== 0)
      .map((t) => (
        <VLine key={t.ms} $frac={t.frac} $color={COLORS.grid} />
      ))}
    <VLine $frac={scale.zeroFrac} $color={COLORS.unlock} />
  </>
);

/** The winner's time carried down through every racer's lane, so each gap is a visible distance. */
const WinnerGuide = styled(motion.div)<{ $frac: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(p) => p.$frac * 100}%;
  width: 2px;
  margin-left: -1px;
  background: ${COLORS.winnerGuide};
`;

function measureDelay(scale: TimeScale, r: Racer): number {
  const start = r.tappedMs ?? r.countedMs;
  return start === null ? START_S + SWEEP_S : START_S + SWEEP_S * scale.x(start);
}

/** When the backwards playhead reaches this racer (0 for the rightmost). */
function rewindDelay(scale: TimeScale, r: Racer): number {
  const x = (measureDelay(scale, r) - START_S) / SWEEP_S;
  return Math.max(0, 1 - x) * SWEEP_S * REWIND;
}

function landDelay(scale: TimeScale, r: Racer): number {
  const d = measureDelay(scale, r);
  return r.shifts.length > 0 ? d + SHIFT_PAUSE_S + SHIFT_STEP_S * r.shifts.length : d + POP_S;
}

function rightLabel(r: Racer): { text: string; tone: 'winner' | 'gap' | 'status' | 'early' } {
  if (r.kind === 'blocked') return { text: 'blocked', tone: 'status' };
  if (r.countedMs === null) return { text: 'hidden', tone: 'status' };
  if (r.kind === 'early') return { text: formatGap(r.countedMs), tone: 'early' };
  if (r.kind === 'late') return { text: 'late', tone: 'status' };
  if (r.isWinner) return { text: formatSeconds(r.countedMs, false), tone: 'winner' };
  if (r.gapMs !== null) return { text: formatGap(r.gapMs), tone: 'gap' };
  // The winner's time is hidden, so there is nothing to measure a gap from.
  return { text: formatSeconds(r.countedMs, false), tone: 'gap' };
}

interface MarksProps {
  racer: Racer;
  scale: TimeScale;
  plotPx: number;
  animate: boolean;
  rewind?: boolean;
}

/** A head start of 120ms on a 3s axis is a few pixels, hidden under the emoji.
 *  Stretch any shift shorter than this away from the marker so it still shows;
 *  the detail card carries the exact numbers. */
const MIN_SHIFT_PX = 18;

function minVisibleShift(fracs: number[], plotPx: number): number[] {
  if (fracs.length < 2 || plotPx <= 0) return fracs;
  const last = fracs[fracs.length - 1];
  const spanPx = (Math.abs(fracs[0] - last) / 100) * plotPx;
  if (spanPx === 0 || spanPx >= MIN_SHIFT_PX) return fracs;
  const k = MIN_SHIFT_PX / spanPx;
  return fracs.map((f) => Math.max(0, Math.min(100, last + (f - last) * k)));
}

/** Which side of the marker the name goes: away from any shift line, and where it fits. */
function nameSide(fracs: number[], username: string, plotPx: number): 'left' | 'right' {
  const f = fracs[fracs.length - 1] / 100;
  const prev = fracs.length > 1 ? fracs[fracs.length - 2] / 100 : null;
  let side: 'left' | 'right' = prev !== null ? (prev < f ? 'right' : 'left') : f > 0.58 ? 'left' : 'right';
  const need = Math.min(120, username.length * 6.4 + 12) + 14;
  const room = (s: 'left' | 'right') => (s === 'right' ? (1 - f) * plotPx : f * plotPx) + TRACK_PAD;
  if (room(side) < need && room(side === 'right' ? 'left' : 'right') > room(side)) {
    side = side === 'right' ? 'left' : 'right';
  }
  return side;
}

/** The tap dot, shift lines and the racer's marker, animated in on mount. */
const Marks: FC<MarksProps> = ({ racer: r, scale, plotPx, animate, rewind }) => {
  const pts = pathOf(r);
  if (pts.length === 0) return null;
  const fracs = minVisibleShift(
    pts.map((ms) => scale.x(ms) * 100),
    plotPx,
  );
  const d = measureDelay(scale, r);
  const moveAt = d + SHIFT_PAUSE_S;
  const moving = fracs.length > 1;
  const finalFrac = fracs[fracs.length - 1];
  const side = nameSide(fracs, r.username, plotPx);
  const dim = r.kind === 'late';

  const ringColor = r.isWinner ? COLORS.winner : r.kind === 'early' ? COLORS.early : null;
  const ringWash = r.isWinner ? COLORS.winnerWash : COLORS.earlyWash;
  const dotColor = r.isWinner ? COLORS.winner : r.kind === 'early' ? COLORS.early : COLORS.dot;

  const leftKeyframes = fracs.map((f) => `${f}%`);
  // rewind: shift lines pull back from the marker, then the marker shrinks away
  const rd = rewindDelay(scale, r);
  const backS = moving ? SHIFT_STEP_S * REWIND * r.shifts.length : 0;
  const goneAt = rd + backS;
  const n = r.shifts.length;
  return (
    <>
      {moving &&
        r.shifts.map((s, i) => {
          const from = fracs[i];
          const to = fracs[i + 1];
          const color = s.deltaMs < 0 ? COLORS.headStart : COLORS.penalty;
          return (
            <Segment
              key={`seg${i}`}
              data-shift={s.deltaMs < 0 ? 'head-start' : 'penalty'}
              $color={color}
              style={{
                left: `${Math.min(from, to)}%`,
                width: `${Math.abs(to - from)}%`,
                transformOrigin: to >= from ? 'left center' : 'right center',
              }}
              initial={animate ? { scaleX: 0 } : false}
              animate={{ scaleX: rewind ? 0 : 1 }}
              transition={
                rewind
                  ? { delay: rd + SHIFT_STEP_S * REWIND * (n - 1 - i), duration: SHIFT_STEP_S * REWIND, ease: 'easeInOut' }
                  : { delay: moveAt + SHIFT_STEP_S * i, duration: SHIFT_STEP_S, ease: 'easeInOut' }
              }
            />
          );
        })}
      {moving && (
        <TapDot
          $color={r.shifts[0].deltaMs < 0 ? COLORS.headStart : COLORS.penalty}
          style={{ left: `${fracs[0]}%` }}
          initial={animate ? { opacity: 0, scale: 0.3 } : false}
          animate={rewind ? { opacity: 0, scale: 0.3 } : { opacity: 1, scale: 1 }}
          transition={rewind ? { delay: goneAt, duration: POP_S * REWIND } : { delay: d, duration: POP_S }}
        />
      )}
      <Pos
        initial={animate ? { left: leftKeyframes[0] } : false}
        animate={{ left: moving ? (rewind ? [...leftKeyframes].reverse() : leftKeyframes) : `${finalFrac}%` }}
        transition={{
          left: moving
            ? rewind
              ? { delay: rd, duration: backS, ease: 'easeInOut' }
              : { delay: moveAt, duration: SHIFT_STEP_S * r.shifts.length, ease: 'easeInOut' }
            : { duration: 0 },
        }}
        style={animate ? undefined : { left: `${finalFrac}%` }}
      >
        <Center>
          <Pop
            initial={animate ? { opacity: 0, scale: 0.4 } : false}
            animate={rewind ? { opacity: 0, scale: 0.4 } : { opacity: 1, scale: 1 }}
            transition={
              rewind
                ? { delay: goneAt, duration: POP_S * REWIND, ease: 'easeIn' }
                : { delay: d, duration: POP_S, ease: [0.34, 1.56, 0.64, 1] }
            }
          >
            {r.emoji ? (
              <>
                {ringColor && <Ring $color={ringColor} $wash={ringWash} />}
                <Glyph $dim={dim}>{r.emoji}</Glyph>
              </>
            ) : (
              <Dot $color={dotColor} $dim={dim} />
            )}
            <NamePill className="race-name" $side={side} $always={!r.emoji}>
              {r.username}
            </NamePill>
          </Pop>
        </Center>
      </Pos>
    </>
  );
};

/** Rows with no position on the axis: hidden times and blocked attempts. */
const NoTimeMarks: FC<{ racer: Racer; delay: number; animate: boolean; rewind?: boolean }> = ({ racer: r, delay, animate, rewind }) => (
  <InlineNote
    initial={animate ? { opacity: 0 } : false}
    animate={{ opacity: rewind ? 0 : 1 }}
    transition={rewind ? { duration: 0.2 } : { delay, duration: 0.3 }}
  >
    {r.emoji && <Glyph style={{ opacity: 0.75 }}>{r.emoji}</Glyph>}
    <InlineName className={r.emoji ? 'race-name-inline' : undefined}>{r.username}</InlineName>
    {r.kind === 'blocked' ? (
      <>
        <span>🔒</span>
        <span>{blockerText(r)}</span>
      </>
    ) : (
      <span style={{ fontStyle: 'italic' }}>time hidden by a card</span>
    )}
  </InlineNote>
);

interface RowProps {
  racer: Racer;
  model: RaceModel;
  scale: TimeScale;
  plotPx: number;
  winnerFrac: number | null;
  winnerLandS: number;
  open: boolean;
  onToggle: (key: string) => void;
  onClose: () => void;
  animate: boolean;
  rewind?: boolean;
  run: string;
  detailId: string;
}

const RacerRow: FC<RowProps> = ({ racer: r, model, scale, plotPx, winnerFrac, winnerLandS, open, onToggle, onClose, animate, rewind, run, detailId }) => {
  const label = rightLabel(r);
  const timed = r.countedMs !== null && r.kind !== 'blocked';
  const labelDelay = timed ? landDelay(scale, r) : START_S + SWEEP_S;
  return (
    <li>
      <RowButton
        type="button"
        $winner={r.isWinner}
        data-racer={r.username}
        aria-expanded={open}
        aria-controls={open ? detailId : undefined}
        aria-label={ariaSummary(r)}
        onClick={() => onToggle(r.key)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <Track>
          <Plot>
            <Backdrop scale={scale} />
            <React.Fragment key={run}>
              {winnerFrac !== null && (
                <WinnerGuide
                  $frac={winnerFrac}
                  initial={animate ? { opacity: 0 } : false}
                  animate={{ opacity: rewind ? 0 : 1 }}
                  transition={rewind ? { duration: 0.2 } : { delay: winnerLandS, duration: 0.3 }}
                />
              )}
              {timed ? (
                <Marks racer={r} scale={scale} plotPx={plotPx} animate={animate} rewind={rewind} />
              ) : (
                <NoTimeMarks racer={r} delay={labelDelay} animate={animate} rewind={rewind} />
              )}
            </React.Fragment>
          </Plot>
        </Track>
        <motion.span
          key={run}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          initial={animate ? { opacity: 0 } : false}
          animate={{ opacity: rewind ? 0 : 1 }}
          transition={rewind ? { duration: 0.2 } : { delay: labelDelay, duration: 0.25 }}
        >
          <RightLabel $tone={label.tone}>{label.text}</RightLabel>
        </motion.span>
      </RowButton>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="detail"
            initial={animate ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={animate ? { height: 0, opacity: 0 } : { opacity: 0, transition: { duration: 0 } }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <RacerDetail racer={r} runnerUp={model.runnerUp} marginMs={model.marginMs} id={detailId} />
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

// ---- width ----

function useWidth(ref: React.RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setWidth(Math.round(el.getBoundingClientRect().width));
    read();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(read);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', read);
    return () => window.removeEventListener('resize', read);
  }, [ref]);
  return width;
}

// ---- component ----

const RaceTimeline: FC<RaceTimelineProps> = ({ race, generationTimeMs, rewind = false, onRewound }) => {
  const model = useMemo(() => buildRaceModel(race, generationTimeMs), [race, generationTimeMs]);
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;

  const axisRef = useRef<HTMLDivElement>(null);
  const trackWidth = useWidth(axisRef);
  // Before the first measurement (and in tests) assume a phone-sized plot.
  const plotPx = trackWidth > 0 ? trackWidth - 2 * TRACK_PAD : 240;
  const scale = useMemo(() => (model ? buildScale(model, plotPx) : null), [model, plotPx]);

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [replays, setReplays] = useState(0);
  const toggle = useCallback((key: string) => setOpenKey((k) => (k === key ? null : key)), []);
  const close = useCallback(() => setOpenKey(null), []);
  const baseId = useId();

  // Rewind: close any open card, run everything backwards, then hand back.
  const maxShifts = model ? Math.max(0, ...[...model.main, ...model.secondary].map((r) => r.shifts.length)) : 0;
  const rewindMs = animate ? (SWEEP_S + SHIFT_STEP_S * maxShifts + POP_S) * REWIND * 1000 + 150 : 0;
  useEffect(() => {
    if (!rewind) return undefined;
    setOpenKey(null);
    const t = setTimeout(() => onRewound?.(), rewindMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewind]);

  if (!model || !scale) return null;

  // Re-keying the marks replays the mount animation.
  const run = String(replays);
  const winnerFrac = model.winner?.countedMs != null ? scale.x(model.winner.countedMs) : null;
  const winnerLandS = model.winner ? landDelay(scale, model.winner) : 0;
  const shifts = [...model.main, ...model.secondary].flatMap((r) => r.shifts);
  const hasHeadStart = shifts.some((s) => s.deltaMs < 0);
  const hasAdded = shifts.some((s) => s.deltaMs > 0);
  const addedLabel = shifts.every((s) => s.deltaMs <= 0 || s.isPenalty) ? 'no-emoji penalty' : 'time added';
  const w = model.winner;
  const racerCount = model.main.length;

  const renderRows = (rows: Racer[], withWinnerLine: boolean) =>
    rows.map((r, i) => (
      <RacerRow
        key={r.key}
        racer={r}
        model={model}
        scale={scale}
        plotPx={plotPx}
        winnerFrac={withWinnerLine ? winnerFrac : null}
        winnerLandS={winnerLandS}
        open={openKey === r.key}
        onToggle={toggle}
        onClose={close}
        animate={animate}
        rewind={rewind}
        run={run}
        detailId={`${baseId}-${withWinnerLine ? 'm' : 's'}${i}`}
      />
    ));

  return (
    <Wrap data-testid="race-graph">
      <TitleRow>
        <Title>race to the aotd</Title>
        {animate && !rewind && (
          <ReplayButton type="button" onClick={() => setReplays((n) => n + 1)} aria-label="replay the race">
            <ReplayIcon />
            replay
          </ReplayButton>
        )}
      </TitleRow>
      <Card role="group" aria-label="race to the aotd">
        {w && (
          <Summary>
            <span>
              <b>{w.emoji ?? w.username}</b> won
              {model.marginMs !== null && (
                <>
                  {' '}
                  by <b>{formatDuration(model.marginMs)}</b>
                </>
              )}
            </span>
            <span>
              · {racerCount} {racerCount === 1 ? 'racer' : 'racers'}
            </span>
          </Summary>
        )}

        <AxisRow aria-hidden="true">
          <Track ref={axisRef}>
            <Plot>
              {scale.breakFrac !== null && <Band $from={scale.breakFrac} style={{ top: 14 }} />}
              {scale.ticks.map((t) => (
                <TickLabel key={t.ms} $frac={t.frac}>
                  {t.label}
                </TickLabel>
              ))}
              {scale.breakFrac !== null && (
                <BreakMark $frac={scale.breakFrac} width="8" height="6" viewBox="0 0 8 6">
                  <path d="M1 6 L4 0 M4 6 L7 0" stroke="#b9bcc6" strokeWidth="1" fill="none" />
                </BreakMark>
              )}
            </Plot>
          </Track>
          <span />
        </AxisRow>

        <Body>
          <List aria-label="racers in finishing order">{renderRows(model.main, true)}</List>
          {model.secondary.length > 0 && (
            <>
              <Caption>didn't count</Caption>
              <List aria-label="taps that didn't count">{renderRows(model.secondary, false)}</List>
            </>
          )}
          {animate && (
            <Grid
              aria-hidden="true"
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            >
              <Track>
                <Plot>
                  {rewind ? (
                    <Playhead
                      key="rewind"
                      initial={{ left: '100%', opacity: 0 }}
                      animate={{ left: '0%', opacity: [0, 1, 1, 0] }}
                      transition={{
                        left: { duration: SWEEP_S * REWIND, ease: 'linear' },
                        opacity: { duration: SWEEP_S * REWIND + 0.15, times: [0, 0.08, 0.85, 1] },
                      }}
                    />
                  ) : (
                    <Playhead
                      key={run}
                      initial={{ left: '0%', opacity: 0 }}
                      animate={{ left: '100%', opacity: [0, 1, 1, 0] }}
                      transition={{
                        left: { delay: START_S, duration: SWEEP_S, ease: 'linear' },
                        opacity: { delay: START_S, duration: SWEEP_S + 0.2, times: [0, 0.08, 0.85, 1] },
                      }}
                    />
                  )}
                </Plot>
              </Track>
            </Grid>
          )}
        </Body>

        <Footer>
          {hasHeadStart && (
            <LegendItem>
              <ShiftKey color={COLORS.headStart} />
              card head start
            </LegendItem>
          )}
          {hasAdded && (
            <LegendItem>
              <ShiftKey color={COLORS.penalty} />
              {addedLabel}
            </LegendItem>
          )}
          {scale.breakFrac !== null && (
            <LegendItem>
              <Swatch />
              squeezed after {formatTick(scale.linearEndMs)}
            </LegendItem>
          )}
          {openKey === null && <Hint>tap a racer for details</Hint>}
        </Footer>
      </Card>
    </Wrap>
  );
};

export default RaceTimeline;
